import { useCallback, useEffect, useState } from 'react';
import { useI18nState } from '../../stores/settings/language-context';
import QuoteToolbar from './toolbar/quote-toolbar';
import QuoteTabs from './tabs/quote-tabs';
import ApprovalDecisionPopup from './approval-decision-popup/approval-decision-popup';
import { useQuoteTabParam } from './useQuoteTabParam';
import { useApprovalDecision } from './useApprovalDecision';
import { IDateRangeFilterValue } from '../../common/date-range-filter/date-range-filter.interface';
import './quote-management-page.scss';

const initialDateFilter: IDateRangeFilterValue = {
  fromDate: new Date(),
  toDate: new Date(),
  dateFilterType: 1
};

export default function QuoteManagementPage() {
  const { localeData } = useI18nState();
  const { activeTab, setActiveTab } = useQuoteTabParam();
  const [searchText, setSearchText] = useState('');
  const [dateFilter, setDateFilter] =
    useState<IDateRangeFilterValue>(initialDateFilter);
  const [selectedQuoteIds, setSelectedQuoteIds] = useState<string[]>([]);
  const [refreshToken, setRefreshToken] = useState(0);

  // Row selection only applies to the Pending for Approval tab (plan §11e) -
  // drop any stale selection whenever the tab changes.
  useEffect(() => {
    setSelectedQuoteIds([]);
  }, [activeTab]);

  const bumpRefreshToken = useCallback(() => {
    setRefreshToken(token => token + 1);
  }, []);

  const {
    isPopupVisible,
    decisionType,
    quoteIds: popupQuoteIds,
    isSubmitting,
    openApprove,
    openReject,
    closePopup,
    submit
  } = useApprovalDecision(bumpRefreshToken);

  const handleBulkApprove = useCallback(() => {
    openApprove(selectedQuoteIds, 'pendingForApproval');
  }, [openApprove, selectedQuoteIds]);

  const handleBulkReject = useCallback(() => {
    openReject(selectedQuoteIds, 'pendingForApproval');
  }, [openReject, selectedQuoteIds]);

  return (
    <div className="quote-management-page" data-testid="quote_management_page">
      <p className="quote-management-page-heading">
        {localeData.QuoteManagement}
      </p>

      <QuoteToolbar
        searchText={searchText}
        onSearchTextChange={setSearchText}
        dateFilter={dateFilter}
        onDateFilterChange={setDateFilter}
      />

      <div className="quote-management-content-area">
        <QuoteTabs
          activeTab={activeTab}
          onTabChange={setActiveTab}
          searchText={searchText}
          dateFilter={dateFilter}
          refreshToken={refreshToken}
          selectedCount={selectedQuoteIds.length}
          onApprove={openApprove}
          onReject={openReject}
          onSelectionChange={setSelectedQuoteIds}
          onBulkApprove={handleBulkApprove}
          onBulkReject={handleBulkReject}
        />
      </div>

      <ApprovalDecisionPopup
        visible={isPopupVisible}
        decisionType={decisionType}
        quoteIds={popupQuoteIds}
        isSubmitting={isSubmitting}
        onCancel={closePopup}
        onSubmit={submit}
      />
    </div>
  );
}
